import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../../../config';
import ImagePlaceholder from '../../Global/ImagePlaceholder';
import Spinner from '../../Global/Spinner';
import BouncingDots from '../../Global/BouncingDots';
import { marked } from 'marked';
import './scss/BoardActivity.scss';

const BoardActivity = ({ boardId }) => {
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [activeFilter, setActiveFilter] = useState('all'); // 'all' or 'comments'
    const [error, setError] = useState(null);

    const loadMoreRef = useRef(null);
    const observerRef = useRef(null);
    const isFetchingRef = useRef(false);
    const lastPageRef = useRef(0);
    const [isInitialized, setIsInitialized] = useState(false);

    // Fetch activities
    const fetchActivities = async (pageNum, filter, reset = false) => {
        if (isFetchingRef.current) return;

        isFetchingRef.current = true;
        const isFirstPage = pageNum === 1;

        if (isFirstPage) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        try {
            const response = await fetch(
                `${config.API_URI}/v1/boards/${boardId}/activities?page=${pageNum}&limit=10&filter=${filter}`,
                {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch activities');
            }

            const data = await response.json();

            if (reset || isFirstPage) {
                setActivities(data.activities);
            } else {
                setActivities(prev => [...prev, ...data.activities]);
            }

            setHasMore(data.hasMore);
            setPage(pageNum);
        } catch (err) {
            console.error('Error fetching board activities:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
            isFetchingRef.current = false;
        }
    };

    // Initial load
    useEffect(() => {
        setActivities([]);
        setPage(1);
        setHasMore(true);
        lastPageRef.current = 0;
        isFetchingRef.current = false;
        fetchActivities(1, activeFilter, true);

        const timer = setTimeout(() => {
            setIsInitialized(true);
        }, 300);

        return () => clearTimeout(timer);
    }, [boardId, activeFilter]);

    // Infinite scroll callback
    const loadMore = useCallback(() => {
        if (isFetchingRef.current || loadingMore || !hasMore || loading || !isInitialized) {
            return;
        }

        const nextPage = page + 1;
        if (lastPageRef.current >= nextPage) {
            return;
        }

        isFetchingRef.current = true;
        lastPageRef.current = nextPage;
        fetchActivities(nextPage, activeFilter);
    }, [page, hasMore, loadingMore, loading, isInitialized, activeFilter]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        if (!isInitialized) return;

        const currentRef = loadMoreRef.current;
        if (!currentRef) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        observerRef.current.observe(currentRef);

        return () => {
            if (observerRef.current && currentRef) {
                observerRef.current.unobserve(currentRef);
            }
        };
    }, [hasMore, loadingMore, loading, loadMore, isInitialized]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        });
    };

    return (
        <div className="board-activity-container">
            <div className="activity-filters">
                <button
                    className={activeFilter === 'all' ? 'active' : ''}
                    onClick={() => setActiveFilter('all')}
                >
                    All
                </button>
                <button
                    className={activeFilter === 'comments' ? 'active' : ''}
                    onClick={() => setActiveFilter('comments')}
                >
                    Comments
                </button>
            </div>

            <div className="activity-content">
                {loading && activities.length === 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <Spinner size={30} color="#3498db" speed={1.4} />
                    </div>
                ) : error ? (
                    <p className="error-message">{error}</p>
                ) : activities.length === 0 ? (
                    <p className="empty-message">No activities found</p>
                ) : (
                    <>
                        <div className="activities-list">
                            {activities.map((activity) => {
                                if (!activity?.initiator?.name) return null;

                                return (
                                    <div key={activity._id} className="activity-item">
                                        {activity?.initiator?.profilePicture ? (
                                            <img
                                                src={activity.initiator.profilePicture}
                                                alt={activity.initiator.name}
                                                className="activity-profile-img"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <ImagePlaceholder
                                                className="activity-profile-img"
                                                size={35}
                                                member={activity.initiator}
                                                text={activity.initiator.name}
                                                onlineIndicator={false}
                                            />
                                        )}

                                        <div className="activity-content-wrapper">
                                            <div className="activity-header">
                                                <strong>{activity.initiator.name}</strong>
                                                <div
                                                    className="activity-details"
                                                    dangerouslySetInnerHTML={{ __html: marked(activity.details) }}
                                                    onClick={(e) => {
                                                        // Handle card link clicks
                                                        if (e.target.tagName === 'A') {
                                                            e.preventDefault();
                                                            const href = e.target.getAttribute('href');
                                                            if (href && href.startsWith('/c/')) {
                                                                navigate(href);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <span className="activity-time">
                                                {formatDate(activity.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Infinite scroll trigger */}
                        {hasMore && (
                            <div ref={loadMoreRef} style={{ height: '20px', margin: '10px 0' }}>
                                {loadingMore && (
                                    <div className="loading-more">
                                        <BouncingDots />
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BoardActivity;

